import { fuelTransactionsService } from './fuel-transactions';
import { shipmentCostingsService } from './shipment-costings';
import { shipmentsService } from './shipments';
import { enhancedFuelImportService } from './fuel-import-enhanced';
import { calculateVATBreakdown } from '../../utils/vat-calculations';

class BulkFuelOperationsService {
  constructor() {
    this.batchSize = 50; // Process in batches to avoid overwhelming the system
  }

  // Get comprehensive fuel cost overview
  async getFuelCostOverview(companyId) {
    try {
      const [transactions, costings, shipments] = await Promise.all([
        fuelTransactionsService.getFuelTransactionsByCompany(companyId),
        shipmentCostingsService.getCostingsByCompany(companyId),
        shipmentsService.getShipments(companyId)
      ]);

      // Create lookup maps for performance
      const costingMap = new Map(costings.map(c => [c.shipmentId, c]));
      const shipmentMap = new Map(shipments.map(s => [s.id, s]));

      // Create a map of applied transactions for quick lookup
      const appliedTransactionsMap = new Map();
      costings.forEach(costing => {
        if (costing.appliedFuelTransactions && Array.isArray(costing.appliedFuelTransactions)) {
          costing.appliedFuelTransactions.forEach(appliedTx => {
            // Create multiple keys for robust matching
            const invoiceNumber = appliedTx.invoice_number;
            const fillTime = appliedTx.fill_time;
            
            // Convert fill_time to consistent format for key generation
            let timeKey = '';
            if (fillTime) {
              if (fillTime.toDate) {
                // Firestore Timestamp
                timeKey = fillTime.toDate().getTime().toString();
              } else if (fillTime instanceof Date) {
                timeKey = fillTime.getTime().toString();
              } else if (typeof fillTime === 'string') {
                timeKey = new Date(fillTime).getTime().toString();
              } else {
                timeKey = fillTime.toString();
              }
            }
            
            const primaryKey = `${invoiceNumber}_${timeKey}`;
            const fallbackKey = `${invoiceNumber}_${fillTime}`;
            
            const appliedData = {
              ...appliedTx,
              costingId: costing.id,
              shipmentId: costing.shipmentId
            };
            
            appliedTransactionsMap.set(primaryKey, appliedData);
            appliedTransactionsMap.set(fallbackKey, appliedData);
            
            // Also create a key with just invoice number for partial matching
            appliedTransactionsMap.set(`invoice_${invoiceNumber}`, appliedData);
          });
        }
      });

      // Enrich transactions with application status and shipment info
      const enrichedTransactions = transactions.map(transaction => {
        const costing = costingMap.get(transaction.shipment_id);
        const shipment = shipmentMap.get(transaction.shipment_id);
        
        // Check if this specific transaction has been applied (by invoice number and time)
        let appliedTransaction = null;
        let isApplied = false;
        
        // Try multiple key formats for robust matching
        const invoiceNumber = transaction.invoice_number;
        const fillTime = transaction.fill_time;
        
        if (invoiceNumber && fillTime) {
          // Convert fill_time to consistent format
          let timeKey = '';
          if (fillTime.toDate) {
            timeKey = fillTime.toDate().getTime().toString();
          } else if (fillTime instanceof Date) {
            timeKey = fillTime.getTime().toString();
          } else if (typeof fillTime === 'string') {
            timeKey = new Date(fillTime).getTime().toString();
          } else {
            timeKey = fillTime.toString();
          }
          
          const primaryKey = `${invoiceNumber}_${timeKey}`;
          const fallbackKey = `${invoiceNumber}_${fillTime}`;
          const invoiceOnlyKey = `invoice_${invoiceNumber}`;
          
          // Try different keys in order of preference
          appliedTransaction = appliedTransactionsMap.get(primaryKey) || 
                              appliedTransactionsMap.get(fallbackKey) || 
                              appliedTransactionsMap.get(invoiceOnlyKey);
          
          isApplied = !!appliedTransaction;
        }
        
        const appliedAmount = appliedTransaction?.cost || 0;
        
        // Check if transaction could be better aligned
        const isMisaligned = this.checkIfMisaligned(transaction, shipments, shipment);
        
        return {
          ...transaction,
          isApplied,
          appliedAmount,
          appliedTransaction,
          shipment,
          costing,
          isMisaligned,
          // Additional metadata
          canBeApplied: !!transaction.shipment_id && !isApplied, // Can only apply if not already applied
          needsReAlignment: isMisaligned,
          applicationStatus: this.getApplicationStatus(transaction, costing, isApplied)
        };
      });

      // Calculate statistics
      const stats = this.calculateOverviewStats(enrichedTransactions);

      return {
        transactions: enrichedTransactions,
        stats,
        summary: {
          totalTransactions: transactions.length,
          totalShipments: shipments.length,
          totalCostings: costings.length,
          lastUpdated: new Date()
        }
      };

    } catch (error) {
      console.error('Error getting fuel cost overview:', error);
      throw error;
    }
  }

  // Check if a transaction has been applied to any costing
  async isTransactionApplied(companyId, transaction) {
    try {
      const costings = await shipmentCostingsService.getCostingsByCompany(companyId);
      
      for (const costing of costings) {
        if (costing.appliedFuelTransactions && Array.isArray(costing.appliedFuelTransactions)) {
          const isApplied = costing.appliedFuelTransactions.some(appliedTx => 
            appliedTx.invoice_number === transaction.invoice_number &&
            appliedTx.fill_time === transaction.fill_time
          );
          
          if (isApplied) {
            return {
              isApplied: true,
              costingId: costing.id,
              shipmentId: costing.shipmentId
            };
          }
        }
      }
      
      return { isApplied: false };
    } catch (error) {
      console.error('Error checking if transaction is applied:', error);
      return { isApplied: false };
    }
  }

  // Get all applied fuel transactions for a company
  async getAppliedFuelTransactions(companyId) {
    try {
      const costings = await shipmentCostingsService.getCostingsByCompany(companyId);
      const appliedTransactions = [];
      
      costings.forEach(costing => {
        if (costing.appliedFuelTransactions && Array.isArray(costing.appliedFuelTransactions)) {
          costing.appliedFuelTransactions.forEach(appliedTx => {
            appliedTransactions.push({
              ...appliedTx,
              costingId: costing.id,
              shipmentId: costing.shipmentId
            });
          });
        }
      });
      
      return appliedTransactions;
    } catch (error) {
      console.error('Error getting applied fuel transactions:', error);
      return [];
    }
  }

  // Check if a transaction could be better aligned
  checkIfMisaligned(transaction, allShipments, currentShipment) {
    if (!transaction.shipment_id || !currentShipment) return false;
    
    try {
      // Use enhanced matching to find the best match
      const betterMatch = enhancedFuelImportService.findMatchingShipmentEnhanced(
        transaction,
        allShipments.filter(s => s.vehiclePlate && s.id !== transaction.shipment_id)
      );

      // If we found a better match, this transaction is misaligned
      return betterMatch && betterMatch.id !== transaction.shipment_id;
    } catch (error) {
      console.error('Error checking alignment for transaction:', transaction.id, error);
      return false;
    }
  }

  // Get application status for a transaction
  getApplicationStatus(transaction, costing, isApplied = false) {
    if (!transaction.shipment_id) return 'unassigned';
    if (isApplied) return 'applied';
    if (!costing) return 'not_applied';
    if (costing.fuel === 0) return 'not_applied';
    if (costing.fuel > 0) return 'applied'; // Legacy check for backward compatibility
    return 'unknown';
  }

  // Calculate overview statistics
  calculateOverviewStats(transactions) {
    const stats = {
      total: transactions.length,
      applied: 0,
      unapplied: 0,
      unassigned: 0,
      misaligned: 0,
      totalCost: 0,
      appliedCost: 0,
      unappliedCost: 0,
      unassignedCost: 0,
      averageCostPerTransaction: 0,
      applicationRate: 0
    };

    transactions.forEach(transaction => {
      const cost = transaction.cost || 0;
      stats.totalCost += cost;

      switch (transaction.applicationStatus) {
        case 'applied':
          stats.applied++;
          stats.appliedCost += cost;
          break;
        case 'not_applied':
          stats.unapplied++;
          stats.unappliedCost += cost;
          break;
        case 'unassigned':
          stats.unassigned++;
          stats.unassignedCost += cost;
          break;
      }

      if (transaction.isMisaligned) {
        stats.misaligned++;
      }
    });

    stats.averageCostPerTransaction = stats.total > 0 ? stats.totalCost / stats.total : 0;
    stats.applicationRate = stats.total > 0 ? stats.applied / stats.total : 0;

    return stats;
  }

  // Check for duplicate fuel transactions based on invoice number and time
  async checkForDuplicates(companyId, transactionIds) {
    try {
      const transactions = await fuelTransactionsService.getFuelTransactionsByCompany(companyId);
      const targetTransactions = transactions.filter(t => transactionIds.includes(t.id));
      
      // Get all existing costings to check for applied transactions
      const existingCostings = await shipmentCostingsService.getCostingsByCompany(companyId);
      
      // Create a map of applied transactions by invoice number and time
      const appliedTransactionsMap = new Map();
      
      // Check each costing for applied fuel transactions
      for (const costing of existingCostings) {
        if (costing.appliedFuelTransactions && Array.isArray(costing.appliedFuelTransactions)) {
          costing.appliedFuelTransactions.forEach(appliedTx => {
            const key = `${appliedTx.invoice_number}_${appliedTx.fill_time}`;
            appliedTransactionsMap.set(key, {
              ...appliedTx,
              costingId: costing.id,
              shipmentId: costing.shipmentId
            });
          });
        }
      }

      // Check for duplicates in target transactions
      const duplicates = [];
      const validTransactions = [];

      for (const transaction of targetTransactions) {
        const key = `${transaction.invoice_number}_${transaction.fill_time}`;
        
        if (appliedTransactionsMap.has(key)) {
          const existingApplication = appliedTransactionsMap.get(key);
          duplicates.push({
            transaction,
            existingApplication,
            reason: 'Already applied to costing'
          });
        } else {
          validTransactions.push(transaction);
        }
      }

      return {
        duplicates,
        validTransactions,
        totalChecked: targetTransactions.length
      };
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      throw error;
    }
  }

  // Bulk apply fuel costs to shipment costings with duplicate prevention
  async bulkApplyFuelCosts(companyId, transactionIds, userId) {
    try {
      console.log(`🔄 Starting bulk apply for ${transactionIds.length} transactions`);
      
      // First, check for duplicates
      const duplicateCheck = await this.checkForDuplicates(companyId, transactionIds);
      
      if (duplicateCheck.duplicates.length > 0) {
        console.log(`⚠️ Found ${duplicateCheck.duplicates.length} duplicate transactions`);
        duplicateCheck.duplicates.forEach(dup => {
          console.log(`🔍 Duplicate: Invoice ${dup.transaction.invoice_number} already applied to shipment ${dup.existingApplication.shipmentId}`);
        });
      }

      // Use only valid (non-duplicate) transactions
      const targetTransactions = duplicateCheck.validTransactions;
      
      if (targetTransactions.length === 0) {
        return {
          success: 0,
          failed: transactionIds.length,
          errors: [{ error: 'All selected transactions are duplicates or invalid', count: transactionIds.length }],
          duplicates: duplicateCheck.duplicates,
          updatedCostings: [],
          createdCostings: []
        };
      }

      // Filter out transactions that don't have shipment assignments
      const assignedTransactions = targetTransactions.filter(t => t.shipment_id);
      const unassignedCount = targetTransactions.length - assignedTransactions.length;
      
      if (unassignedCount > 0) {
        console.log(`⚠️ Skipping ${unassignedCount} unassigned transactions`);
      }

      if (assignedTransactions.length === 0) {
        return {
          success: 0,
          failed: targetTransactions.length + duplicateCheck.duplicates.length,
          errors: [{ error: 'All valid transactions are unassigned to shipments', count: targetTransactions.length }],
          duplicates: duplicateCheck.duplicates,
          updatedCostings: [],
          createdCostings: []
        };
      }

      // Group transactions by shipment
      const transactionsByShipment = new Map();
      assignedTransactions.forEach(transaction => {
        if (!transactionsByShipment.has(transaction.shipment_id)) {
          transactionsByShipment.set(transaction.shipment_id, []);
        }
        transactionsByShipment.get(transaction.shipment_id).push(transaction);
      });

      console.log(`📊 Grouped ${assignedTransactions.length} transactions into ${transactionsByShipment.size} shipments`);

      // Get existing costings
      let existingCostings = [];
      try {
        existingCostings = await shipmentCostingsService.getCostingsByCompany(companyId);
      } catch (error) {
        console.log('No existing costings found, will create new ones');
        existingCostings = [];
      }
      
      const costingMap = new Map(existingCostings.map(c => [c.shipmentId, c]));

      const results = {
        success: 0,
        failed: unassignedCount + duplicateCheck.duplicates.length, // Count unassigned and duplicates as failed
        errors: [],
        duplicates: duplicateCheck.duplicates,
        updatedCostings: [],
        createdCostings: []
      };

      // Add unassigned error if any
      if (unassignedCount > 0) {
        results.errors.push({
          error: `${unassignedCount} transactions are not assigned to shipments`,
          count: unassignedCount
        });
      }

      // Add duplicate error if any
      if (duplicateCheck.duplicates.length > 0) {
        results.errors.push({
          error: `${duplicateCheck.duplicates.length} transactions are duplicates (already applied)`,
          count: duplicateCheck.duplicates.length
        });
      }

      // Process each shipment
      for (const [shipmentId, shipmentTransactions] of transactionsByShipment) {
        try {
          const totalFuelCost = shipmentTransactions.reduce((sum, t) => sum + (parseFloat(t.cost) || 0), 0);
          const existingCosting = costingMap.get(shipmentId);

          // Prepare transaction records for tracking
          const transactionRecords = shipmentTransactions.map(tx => ({
            id: tx.id,
            invoice_number: tx.invoice_number,
            fill_time: tx.fill_time,
            cost: tx.cost,
            vehicle_plate: tx.vehicle_plate,
            mappedVehiclePlate: tx.mappedVehiclePlate,
            station_name: tx.station_name,
            liters: tx.liters,
            applied_at: new Date(),
            applied_by: userId
          }));

          if (existingCosting) {
            // Update existing costing - add to PetroApp fuel cost (separate from manual fuel)
            const currentPetroAppFuelAmount = parseFloat(existingCosting.petroAppFuelAmount) || 0;
            const currentManualFuelAmount = parseFloat(existingCosting.manualFuelAmount) || 0;
            const newPetroAppFuelAmount = currentPetroAppFuelAmount + totalFuelCost;
            
            // Get existing applied transactions and check for duplicates
            const existingAppliedTransactions = existingCosting.appliedFuelTransactions || [];
            
            // Filter out any transactions that are already applied (by invoice number and time)
            const newTransactionRecords = transactionRecords.filter(newTx => {
              return !existingAppliedTransactions.some(existingTx => 
                existingTx.invoice_number === newTx.invoice_number &&
                existingTx.fill_time === newTx.fill_time
              );
            });
            
            if (newTransactionRecords.length === 0) {
              console.log(`⚠️ All transactions for shipment ${shipmentId} are already applied, skipping`);
              results.failed += shipmentTransactions.length;
              results.errors.push({
                shipmentId,
                error: 'All transactions are already applied to this shipment',
                transactionCount: shipmentTransactions.length
              });
              continue;
            }
            
            const updatedAppliedTransactions = [...existingAppliedTransactions, ...newTransactionRecords];
            const actualAddedCost = newTransactionRecords.reduce((sum, tx) => sum + (parseFloat(tx.cost) || 0), 0);
            const actualNewPetroAppFuelAmount = currentPetroAppFuelAmount + actualAddedCost;
            
            // Calculate VAT breakdown for PetroApp fuel (assuming fuel includes VAT by default)
            const petroAppFuelVATBreakdown = this.calculateFuelVATBreakdown(actualNewPetroAppFuelAmount, true);
            const manualFuelVATBreakdown = this.calculateFuelVATBreakdown(currentManualFuelAmount, existingCosting.manualFuelIncludesVAT !== false);
            
            // Calculate combined totals
            const totalFuelAmount = actualNewPetroAppFuelAmount + currentManualFuelAmount;
            const totalFuelBaseAmount = petroAppFuelVATBreakdown.baseAmount + manualFuelVATBreakdown.baseAmount;
            const totalFuelVATAmount = petroAppFuelVATBreakdown.vatAmount + manualFuelVATBreakdown.vatAmount;
            
            console.log(`💰 Updating costing for shipment ${shipmentId}:`, {
              previousPetroAppAmount: currentPetroAppFuelAmount,
              addedPetroAppAmount: actualAddedCost,
              newPetroAppAmount: actualNewPetroAppFuelAmount,
              manualFuelAmount: currentManualFuelAmount,
              totalFuelAmount: totalFuelAmount,
              petroAppVATBreakdown: petroAppFuelVATBreakdown,
              newTransactionsCount: newTransactionRecords.length,
              totalTransactionsCount: updatedAppliedTransactions.length,
              skippedDuplicates: shipmentTransactions.length - newTransactionRecords.length
            });
            
            await shipmentCostingsService.updateCosting(existingCosting.id, {
              // PetroApp Fuel (separate tracking)
              petroAppFuelAmount: actualNewPetroAppFuelAmount,
              petroAppFuelIncludesVAT: true,
              petroAppFuelBaseAmount: petroAppFuelVATBreakdown.baseAmount,
              petroAppFuelVATAmount: petroAppFuelVATBreakdown.vatAmount,
              
              // Combined Fuel Totals
              totalFuelAmount: totalFuelAmount,
              totalFuelBaseAmount: totalFuelBaseAmount,
              totalFuelVATAmount: totalFuelVATAmount,
              
              // Track applied fuel transactions to prevent duplicates
              appliedFuelTransactions: updatedAppliedTransactions,
              
              // Legacy fields for backward compatibility
              fuel: totalFuelAmount,
              fuelAmount: totalFuelAmount,
              fuelIncludesVAT: true,
              fuelBaseAmount: totalFuelBaseAmount,
              fuelVATAmount: totalFuelVATAmount,
              
              costingStatus: totalFuelAmount > 0 ? 'partial' : 'not_added',
              updatedAt: new Date(),
              updatedBy: userId
            });

            results.updatedCostings.push({
              shipmentId,
              costingId: existingCosting.id,
              previousFuelCost: currentPetroAppFuelAmount,
              newFuelCost: totalFuelAmount,
              addedAmount: actualAddedCost,
              transactionCount: newTransactionRecords.length,
              skippedDuplicates: shipmentTransactions.length - newTransactionRecords.length,
              appliedTransactions: newTransactionRecords
            });

            results.success += newTransactionRecords.length;
            if (shipmentTransactions.length - newTransactionRecords.length > 0) {
              results.failed += shipmentTransactions.length - newTransactionRecords.length;
              results.errors.push({
                shipmentId,
                error: `${shipmentTransactions.length - newTransactionRecords.length} transactions were duplicates and skipped`,
                transactionCount: shipmentTransactions.length - newTransactionRecords.length
              });
            }

          } else {
            // Create new costing
            const petroAppFuelVATBreakdown = this.calculateFuelVATBreakdown(totalFuelCost, true);
            console.log(`💰 Creating new costing for shipment ${shipmentId}:`, {
              petroAppFuelAmount: totalFuelCost,
              vatBreakdown: petroAppFuelVATBreakdown,
              transactionsCount: transactionRecords.length
            });
            
            const costingData = {
              companyId,
              shipmentId,
              
              // Manual Fuel (empty for new PetroApp-only costing)
              manualFuelAmount: 0,
              manualFuelIncludesVAT: true,
              manualFuelBaseAmount: 0,
              manualFuelVATAmount: 0,
              
              // PetroApp Fuel (from imported transactions)
              petroAppFuelAmount: totalFuelCost,
              petroAppFuelIncludesVAT: true,
              petroAppFuelBaseAmount: petroAppFuelVATBreakdown.baseAmount,
              petroAppFuelVATAmount: petroAppFuelVATBreakdown.vatAmount,
              
              // Combined Fuel Totals
              totalFuelAmount: totalFuelCost,
              totalFuelBaseAmount: petroAppFuelVATBreakdown.baseAmount,
              totalFuelVATAmount: petroAppFuelVATBreakdown.vatAmount,
              
              // Other costs
              maintenanceAmount: 0,
              maintenanceIncludesVAT: true,
              maintenanceBaseAmount: 0,
              maintenanceVATAmount: 0,
              commissionAmount: 0,
              stops: [],
              otherCosts: [],
              
              // Track applied fuel transactions to prevent duplicates
              appliedFuelTransactions: transactionRecords,
              
              // Legacy fields for backward compatibility
              fuel: totalFuelCost,
              maintenance: 0,
              commission: 0,
              fuelAmount: totalFuelCost,
              fuelIncludesVAT: true,
              fuelBaseAmount: petroAppFuelVATBreakdown.baseAmount,
              fuelVATAmount: petroAppFuelVATBreakdown.vatAmount,
              
              costingStatus: 'partial',
              createdAt: new Date(),
              createdBy: userId
            };

            const newCosting = await shipmentCostingsService.addCosting(costingData);

            results.createdCostings.push({
              shipmentId,
              costingId: newCosting.id || 'created',
              fuelCost: totalFuelCost,
              transactionCount: shipmentTransactions.length,
              appliedTransactions: transactionRecords
            });
          }

          results.success += shipmentTransactions.length;
          console.log(`✅ Applied fuel costs for shipment ${shipmentId}: ${totalFuelCost} SAR (${shipmentTransactions.length} transactions)`);

        } catch (error) {
          console.error(`❌ Error processing shipment ${shipmentId}:`, error);
          results.failed += shipmentTransactions.length;
          results.errors.push({
            shipmentId,
            error: error.message,
            transactionCount: shipmentTransactions.length
          });
        }
      }

      console.log(`✅ Bulk apply completed: ${results.success} success, ${results.failed} failed`);
      
      if (results.errors.length > 0) {
        console.log('❌ Errors encountered:', results.errors);
      }

      return results;

    } catch (error) {
      console.error('Error in bulk apply fuel costs:', error);
      throw error;
    }
  }

  // Bulk re-align fuel transactions to better matching shipments
  async bulkReAlignFuelTransactions(companyId, transactionIds, userId) {
    try {
      console.log(`🔄 Starting bulk re-alignment for ${transactionIds.length} transactions`);

      // Get all necessary data
      const [transactions, shipments] = await Promise.all([
        fuelTransactionsService.getFuelTransactionsByCompany(companyId),
        shipmentsService.getShipments(companyId)
      ]);

      const targetTransactions = transactionIds.length > 0 
        ? transactions.filter(t => transactionIds.includes(t.id))
        : transactions; // If no specific IDs, re-align all

      const shipmentsWithVehicles = shipments.filter(s => s.vehiclePlate);

      const reAlignmentResults = [];
      const updates = [];

      // Find better matches for each transaction
      for (const transaction of targetTransactions) {
        try {
          const betterMatch = enhancedFuelImportService.findMatchingShipmentEnhanced(
            transaction,
            shipmentsWithVehicles
          );

          if (betterMatch && betterMatch.id !== transaction.shipment_id) {
            const currentShipment = shipments.find(s => s.id === transaction.shipment_id);
            
            reAlignmentResults.push({
              transactionId: transaction.id,
              currentShipmentId: transaction.shipment_id,
              newShipmentId: betterMatch.id,
              improvement: this.calculateMatchImprovement(transaction, betterMatch),
              transaction,
              currentShipment,
              betterMatch
            });

            updates.push({
              id: transaction.id,
              data: {
                shipment_id: betterMatch.id,
                updated_by: userId,
                re_aligned: true,
                re_aligned_at: new Date(),
                re_aligned_from: transaction.shipment_id
              }
            });
          }
        } catch (error) {
          console.error(`Error finding better match for transaction ${transaction.id}:`, error);
        }
      }

      console.log(`📊 Found ${reAlignmentResults.length} re-alignment opportunities`);

      // Apply the re-alignments if any found
      if (updates.length > 0) {
        await fuelTransactionsService.bulkUpdateFuelTransactions(updates);
        console.log(`✅ Applied ${updates.length} re-alignments`);
      }

      return {
        reAlignmentCount: reAlignmentResults.length,
        results: reAlignmentResults,
        summary: {
          totalAnalyzed: targetTransactions.length,
          reAligned: updates.length,
          noImprovementFound: targetTransactions.length - updates.length
        }
      };

    } catch (error) {
      console.error('Error in bulk re-align fuel transactions:', error);
      throw error;
    }
  }

  // Calculate match improvement score
  calculateMatchImprovement(transaction, newShipment) {
    try {
      const transactionTime = transaction.fill_time?.toDate ? 
        transaction.fill_time.toDate() : 
        new Date(transaction.fill_time);
      
      const shipmentTime = newShipment.deliveryDate ? 
        new Date(newShipment.deliveryDate) : 
        new Date(newShipment.createdAt?.seconds ? newShipment.createdAt.seconds * 1000 : newShipment.createdAt);

      const timeDiff = Math.abs(transactionTime - shipmentTime) / (1000 * 60 * 60 * 24); // days
      
      if (timeDiff === 0) return 'Same Day';
      if (timeDiff <= 1) return 'Next Day';
      if (timeDiff <= 7) return 'Same Week';
      return `${Math.round(timeDiff)} days difference`;
    } catch (error) {
      console.error('Error calculating match improvement:', error);
      return 'Unknown improvement';
    }
  }

  // Calculate fuel VAT breakdown
  calculateFuelVATBreakdown(amount, includesVAT = true) {
    try {
      const result = calculateVATBreakdown(amount, includesVAT);
      
      // Validate the calculation
      const calculatedTotal = result.baseAmount + result.vatAmount;
      const tolerance = 0.01; // 1 cent tolerance
      
      if (includesVAT) {
        // When VAT is included, the total should equal the input amount
        if (Math.abs(result.total - amount) > tolerance) {
          console.warn('VAT calculation mismatch (inclusive):', {
            inputAmount: amount,
            calculatedTotal: result.total,
            difference: Math.abs(result.total - amount)
          });
        }
      } else {
        // When VAT is excluded, base + VAT should equal total
        if (Math.abs(calculatedTotal - result.total) > tolerance) {
          console.warn('VAT calculation mismatch (exclusive):', {
            baseAmount: result.baseAmount,
            vatAmount: result.vatAmount,
            calculatedTotal,
            resultTotal: result.total,
            difference: Math.abs(calculatedTotal - result.total)
          });
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error calculating fuel VAT breakdown:', error);
      // Return safe fallback values
      if (includesVAT) {
        const baseAmount = amount / 1.15;
        const vatAmount = amount - baseAmount;
        return {
          baseAmount: parseFloat(baseAmount.toFixed(2)),
          vatAmount: parseFloat(vatAmount.toFixed(2)),
          total: parseFloat(amount.toFixed(2))
        };
      } else {
        const vatAmount = amount * 0.15;
        const total = amount + vatAmount;
        return {
          baseAmount: parseFloat(amount.toFixed(2)),
          vatAmount: parseFloat(vatAmount.toFixed(2)),
          total: parseFloat(total.toFixed(2))
        };
      }
    }
  }

  // Update costings after transaction realignment
  async updateCostingsAfterRealignment(companyId, realignmentResults, userId) {
    console.log('🔄 Starting costing updates after realignment...');
    
    const results = {
      success: 0,
      failed: 0,
      errors: [],
      updatedCostings: [],
      removedFromCostings: [],
      addedToCostings: []
    };

    try {
      // Import required services
      const { shipmentCostingsService } = await import('./shipment-costings');
      
      // Group transactions by old and new shipments
      const affectedShipments = new Map();
      
      for (const result of realignmentResults) {
        const { transaction, currentShipment, betterMatch } = result;
        
        // Track old shipment (to remove transaction from)
        if (currentShipment?.id) {
          if (!affectedShipments.has(currentShipment.id)) {
            affectedShipments.set(currentShipment.id, { remove: [], add: [] });
          }
          affectedShipments.get(currentShipment.id).remove.push(transaction);
        }
        
        // Track new shipment (to add transaction to)
        if (betterMatch?.id) {
          if (!affectedShipments.has(betterMatch.id)) {
            affectedShipments.set(betterMatch.id, { remove: [], add: [] });
          }
          affectedShipments.get(betterMatch.id).add.push(transaction);
        }
      }
      
      console.log(`📊 Processing ${affectedShipments.size} affected shipments...`);
      
      // Process each affected shipment
      for (const [shipmentId, { remove, add }] of affectedShipments) {
        try {
          const existingCosting = await shipmentCostingsService.getCostingByShipment(shipmentId);
          
          if (existingCosting) {
            // Update existing costing
            await this.updateCostingWithTransactionChanges(
              existingCosting, 
              { remove, add }, 
              userId, 
              results
            );
          } else if (add.length > 0) {
            // Create new costing if transactions are being added to a shipment without costing
            await this.createCostingWithTransactions(
              shipmentId, 
              companyId, 
              add, 
              userId, 
              results
            );
          }
          
        } catch (error) {
          console.error(`❌ Error updating costing for shipment ${shipmentId}:`, error);
          results.failed++;
          results.errors.push({
            shipmentId,
            error: error.message
          });
        }
      }
      
      console.log(`✅ Costing updates complete: ${results.success} success, ${results.failed} failed`);
      return results;
      
    } catch (error) {
      console.error('❌ Error in bulk costing updates after realignment:', error);
      throw error;
    }
  }

  // Helper: Update existing costing with transaction changes
  async updateCostingWithTransactionChanges(existingCosting, { remove, add }, userId, results) {
    const { shipmentCostingsService } = await import('./shipment-costings');
    
    // COMPLETELY REPLACE applied transactions instead of adding/removing
    // This prevents accumulation of duplicates during realignment
    const appliedTransactions = [];
    
    // Add transactions that were realigned to this shipment
    add.forEach(transaction => {
      const transactionRecord = {
        id: transaction.id,
        invoice_number: transaction.invoice_number,
        fill_time: transaction.fill_time,
        cost: parseFloat(transaction.cost) || 0,
        fuel_quantity: parseFloat(transaction.fuel_quantity) || 0,
        vehicle_plate: transaction.vehicle_plate,
        applied_at: new Date(),
        applied_by: userId
      };
      
      appliedTransactions.push(transactionRecord);
      
      results.addedToCostings.push({
        shipmentId: existingCosting.shipmentId,
        transaction: transaction.invoice_number,
        amount: transaction.cost
      });
    });
    
    // Track removed transactions for reporting
    remove.forEach(transaction => {
      results.removedFromCostings.push({
        shipmentId: existingCosting.shipmentId,
        transaction: transaction.invoice_number,
        amount: transaction.cost
      });
    });
    
    // Recalculate PetroApp fuel amounts
    const petroAppFuelAmount = appliedTransactions.reduce((sum, tx) => sum + (parseFloat(tx.cost) || 0), 0);
    const currentManualFuelAmount = parseFloat(existingCosting.manualFuelAmount) || 0;
    const totalFuelAmount = petroAppFuelAmount + currentManualFuelAmount;
    
    // Calculate VAT breakdown
    const petroAppFuelVATBreakdown = this.calculateFuelVATBreakdown(petroAppFuelAmount, true);
    const manualFuelVATBreakdown = this.calculateFuelVATBreakdown(currentManualFuelAmount, 
      existingCosting.manualFuelIncludesVAT !== false);
    
    const totalFuelBaseAmount = petroAppFuelVATBreakdown.baseAmount + manualFuelVATBreakdown.baseAmount;
    const totalFuelVATAmount = petroAppFuelVATBreakdown.vatAmount + manualFuelVATBreakdown.vatAmount;
    
    // Update the costing
    await shipmentCostingsService.updateCosting(existingCosting.id, {
      // PetroApp Fuel (recalculated after realignment)
      petroAppFuelAmount,
      petroAppFuelIncludesVAT: true,
      petroAppFuelBaseAmount: petroAppFuelVATBreakdown.baseAmount,
      petroAppFuelVATAmount: petroAppFuelVATBreakdown.vatAmount,
      
      // Combined Fuel Totals
      totalFuelAmount,
      totalFuelBaseAmount,
      totalFuelVATAmount,
      
      // Track applied fuel transactions
      appliedFuelTransactions: appliedTransactions,
      
      // Legacy fields for backward compatibility
      fuel: totalFuelAmount,
      fuelAmount: totalFuelAmount,
      fuelIncludesVAT: true,
      fuelBaseAmount: totalFuelBaseAmount,
      fuelVATAmount: totalFuelVATAmount,
      
      costingStatus: totalFuelAmount > 0 || currentManualFuelAmount > 0 ? 'partial' : 'not_added',
      updatedAt: new Date(),
      updatedBy: userId,
      realignmentUpdated: true,
      realignmentUpdatedAt: new Date()
    });
    
    results.updatedCostings.push({
      shipmentId: existingCosting.shipmentId,
      costingId: existingCosting.id,
      previousPetroAppAmount: existingCosting.petroAppFuelAmount || 0,
      newPetroAppAmount: petroAppFuelAmount,
      totalFuelAmount,
      removedTransactions: remove.length,
      addedTransactions: add.length
    });
    
    results.success++;
  }

  // Helper: Create new costing with transactions
  async createCostingWithTransactions(shipmentId, companyId, transactions, userId, results) {
    const { shipmentCostingsService } = await import('./shipment-costings');
    
    // Calculate total cost from transactions
    const petroAppFuelAmount = transactions.reduce((sum, tx) => sum + (parseFloat(tx.cost) || 0), 0);
    
    if (petroAppFuelAmount <= 0) return; // Don't create costing for zero amount
    
    // Calculate VAT breakdown
    const petroAppFuelVATBreakdown = this.calculateFuelVATBreakdown(petroAppFuelAmount, true);
    
    // Create transaction records
    const appliedTransactions = transactions.map(transaction => ({
      id: transaction.id,
      invoice_number: transaction.invoice_number,
      fill_time: transaction.fill_time,
      cost: parseFloat(transaction.cost) || 0,
      fuel_quantity: parseFloat(transaction.fuel_quantity) || 0,
      vehicle_plate: transaction.vehicle_plate,
      applied_at: new Date(),
      applied_by: userId
    }));
    
    // Create new costing
    const newCostingData = {
      companyId,
      shipmentId,
      
      // PetroApp Fuel Costs
      petroAppFuelAmount,
      petroAppFuelIncludesVAT: true,
      petroAppFuelBaseAmount: petroAppFuelVATBreakdown.baseAmount,
      petroAppFuelVATAmount: petroAppFuelVATBreakdown.vatAmount,
      appliedFuelTransactions: appliedTransactions,
      
      // Manual Fuel Costs (empty for new costing)
      manualFuelAmount: 0,
      manualFuelIncludesVAT: true,
      manualFuelBaseAmount: 0,
      manualFuelVATAmount: 0,
      
      // Combined Fuel Totals
      totalFuelAmount: petroAppFuelAmount,
      totalFuelBaseAmount: petroAppFuelVATBreakdown.baseAmount,
      totalFuelVATAmount: petroAppFuelVATBreakdown.vatAmount,
      
      // Other costs
      maintenanceAmount: 0,
      commissionAmount: 0,
      stops: [],
      otherCosts: [],
      
      // Legacy fields for backward compatibility
      fuel: petroAppFuelAmount,
      fuelAmount: petroAppFuelAmount,
      fuelIncludesVAT: true,
      fuelBaseAmount: petroAppFuelVATBreakdown.baseAmount,
      fuelVATAmount: petroAppFuelVATBreakdown.vatAmount,
      
      costingStatus: 'partial',
      notes: 'Created automatically from realigned PetroApp transactions',
      createdBy: userId,
      realignmentCreated: true,
      realignmentCreatedAt: new Date()
    };
    
    const newCosting = await shipmentCostingsService.addCosting(newCostingData);
    
    results.updatedCostings.push({
      shipmentId,
      costingId: newCosting.id,
      previousPetroAppAmount: 0,
      newPetroAppAmount: petroAppFuelAmount,
      totalFuelAmount: petroAppFuelAmount,
      addedTransactions: transactions.length,
      isNewCosting: true
    });
    
    results.success++;
  }

  // Remove duplicate fuel applications (cleanup utility)
  async removeDuplicateFuelApplications(companyId, userId) {
    try {
      console.log('🔄 Starting duplicate fuel application cleanup');
      
      const costings = await shipmentCostingsService.getCostingsByCompany(companyId);
      const cleanupResults = {
        costingsProcessed: 0,
        duplicatesRemoved: 0,
        costingsUpdated: 0
      };

      for (const costing of costings) {
        if (costing.appliedFuelTransactions && Array.isArray(costing.appliedFuelTransactions)) {
          const originalCount = costing.appliedFuelTransactions.length;
          
          // Remove duplicates based on invoice number and fill time
          const uniqueTransactions = [];
          const seenKeys = new Set();
          
          costing.appliedFuelTransactions.forEach(tx => {
            const key = `${tx.invoice_number}_${tx.fill_time}`;
            if (!seenKeys.has(key)) {
              seenKeys.add(key);
              uniqueTransactions.push(tx);
            }
          });
          
          if (uniqueTransactions.length < originalCount) {
            const duplicatesCount = originalCount - uniqueTransactions.length;
            console.log(`🧹 Removing ${duplicatesCount} duplicates from costing ${costing.id}`);
            
            // Recalculate PetroApp fuel amount based on unique transactions
            const newPetroAppFuelAmount = uniqueTransactions.reduce((sum, tx) => sum + (parseFloat(tx.cost) || 0), 0);
            const petroAppFuelVATBreakdown = this.calculateFuelVATBreakdown(newPetroAppFuelAmount, true);
            
            // Keep manual fuel amount unchanged
            const manualFuelAmount = parseFloat(costing.manualFuelAmount) || 0;
            const manualFuelVATBreakdown = this.calculateFuelVATBreakdown(manualFuelAmount, costing.manualFuelIncludesVAT !== false);
            
            // Calculate combined totals
            const totalFuelAmount = newPetroAppFuelAmount + manualFuelAmount;
            const totalFuelBaseAmount = petroAppFuelVATBreakdown.baseAmount + manualFuelVATBreakdown.baseAmount;
            const totalFuelVATAmount = petroAppFuelVATBreakdown.vatAmount + manualFuelVATBreakdown.vatAmount;
            
            await shipmentCostingsService.updateCosting(costing.id, {
              // PetroApp Fuel (recalculated)
              petroAppFuelAmount: newPetroAppFuelAmount,
              petroAppFuelBaseAmount: petroAppFuelVATBreakdown.baseAmount,
              petroAppFuelVATAmount: petroAppFuelVATBreakdown.vatAmount,
              
              // Combined Fuel Totals
              totalFuelAmount: totalFuelAmount,
              totalFuelBaseAmount: totalFuelBaseAmount,
              totalFuelVATAmount: totalFuelVATAmount,
              
              // Applied transactions (cleaned)
              appliedFuelTransactions: uniqueTransactions,
              
              // Legacy fields for backward compatibility
              fuel: totalFuelAmount,
              fuelAmount: totalFuelAmount,
              fuelBaseAmount: totalFuelBaseAmount,
              fuelVATAmount: totalFuelVATAmount,
              
              updatedAt: new Date(),
              updatedBy: userId
            });
            
            cleanupResults.duplicatesRemoved += duplicatesCount;
            cleanupResults.costingsUpdated++;
          }
          
          cleanupResults.costingsProcessed++;
        }
      }

      console.log(`✅ Cleanup complete:`, cleanupResults);
      return cleanupResults;
    } catch (error) {
      console.error('Error removing duplicate fuel applications:', error);
      throw error;
    }
  }

  // Auto-apply fuel costs for new shipments
  async autoApplyFuelCostsForNewShipments(companyId, userId) {
    try {
      console.log('🔄 Starting auto-apply for new shipments');

      // Get unassigned fuel transactions
      const transactions = await fuelTransactionsService.getFuelTransactionsByCompany(companyId);
      const unassignedTransactions = transactions.filter(t => !t.shipment_id);

      if (unassignedTransactions.length === 0) {
        console.log('ℹ️ No unassigned transactions found');
        return { applied: 0, message: 'No unassigned transactions found' };
      }

      // Get all shipments
      const shipments = await shipmentsService.getShipments(companyId);
      const shipmentsWithVehicles = shipments.filter(s => s.vehiclePlate);

      // Try to match unassigned transactions
      const matchResults = await enhancedFuelImportService.autoMatchTransactions(
        companyId, 
        unassignedTransactions
      );

      // Apply the matches
      const updates = matchResults.matched.map(transaction => ({
        id: transaction.id,
        data: {
          shipment_id: transaction.shipment_id,
          matched: true,
          updated_by: userId,
          auto_applied: true,
          auto_applied_at: new Date()
        }
      }));

      if (updates.length > 0) {
        await fuelTransactionsService.bulkUpdateFuelTransactions(updates);
        
        // Now apply the costs to costings
        const transactionIds = updates.map(u => u.id);
        const applyResults = await this.bulkApplyFuelCosts(companyId, transactionIds, userId);
        
        console.log(`✅ Auto-applied ${updates.length} transactions to costings`);
        
        return {
          matched: updates.length,
          applied: applyResults.success,
          failed: applyResults.failed,
          message: `Auto-applied ${updates.length} fuel transactions to ${applyResults.success} shipments`
        };
      }

      return { applied: 0, message: 'No new matches found' };

    } catch (error) {
      console.error('Error in auto-apply fuel costs:', error);
      throw error;
    }
  }
}

export const bulkFuelOperationsService = new BulkFuelOperationsService();